import { isSupportedLanguage, stripLanguageFromPathname } from '@/features/i18n';

import { getOrCreateAnalyticsSessionId } from './session';

export type AnalyticsRouteKey =
  | 'landing'
  | 'public_knowledge'
  | 'blog_index'
  | 'blog_article'
  | 'demo'
  | 'login'
  | 'studio_list'
  | 'studio_room'
  | 'share'
  | 'guitar_play'
  | 'practice_song'
  | 'practice_room';

export type AnalyticsEventName =
  | 'landing_view'
  | 'landing_demo_click'
  | 'landing_login_click'
  | 'public_knowledge_view'
  | 'blog_index_view'
  | 'blog_article_view'
  | 'blog_demo_click'
  | 'demo_view'
  | 'demo_play'
  | 'demo_audio_30s'
  | 'demo_loop_used'
  | 'demo_stem_mix_changed'
  | 'login_view'
  | 'studio_list_view'
  | 'studio_room_view'
  | 'studio_play'
  | 'studio_audio_30s'
  | 'studio_loop_used'
  | 'studio_stem_mix_changed'
  | 'share_view'
  | 'guitar_play_view'
  | 'practice_song_view'
  | 'practice_preview_play'
  | 'practice_room_open'
  | 'practice_audio_30s'
  | 'practice_loop_used'
  | 'practice_stem_mix_changed'
  | 'practice_score_opened'
  | 'practice_signup_click';

export interface AnalyticsProperties {
  separation_id?: number;
  engine?: string;
  selected_stem_count?: number;
  guitar_mode?: string;
  invite_present?: boolean;
  is_demo?: boolean;
  is_share?: boolean;
  song_key?: string;
  blog_path?: string;
  landing_path?: string;
  placement?: 'hero' | 'header' | 'final';
}

interface AnalyticsPayload {
  event_id: string;
  session_id: string;
  event_name: AnalyticsEventName;
  source: 'frontend';
  route_key?: AnalyticsRouteKey;
  referrer_host?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  properties?: AnalyticsProperties;
  occurred_at: string;
}

const SONG_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
// Catalog membership is checked again by the server. Never send arbitrary URLs.
const BLOG_PATH_PATTERN = /^\/([a-z]{2})\/blog(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)?$/;

function randomEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function cleanText(value: string | null | undefined, maxLength: number): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, maxLength);
}

function readUtmParam(name: string): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return cleanText(new URLSearchParams(window.location.search).get(name), 160);
}

function referrerHost(): string | undefined {
  if (typeof document === 'undefined' || !document.referrer) {
    return undefined;
  }
  try {
    return cleanText(new URL(document.referrer).host, 200);
  } catch {
    return undefined;
  }
}

function sanitizeProperties(properties?: AnalyticsProperties): AnalyticsProperties | undefined {
  if (!properties) return undefined;
  const sanitized: AnalyticsProperties = {};
  if (Number.isSafeInteger(properties.separation_id) && (properties.separation_id ?? 0) > 0) {
    sanitized.separation_id = properties.separation_id;
  }
  if (typeof properties.engine === 'string' && properties.engine.trim()) {
    sanitized.engine = properties.engine.trim().slice(0, 80);
  }
  if (
    typeof properties.selected_stem_count === 'number' &&
    Number.isFinite(properties.selected_stem_count)
  ) {
    sanitized.selected_stem_count = Math.max(0, Math.min(Math.trunc(properties.selected_stem_count), 12));
  }
  if (typeof properties.guitar_mode === 'string' && properties.guitar_mode.trim()) {
    sanitized.guitar_mode = properties.guitar_mode.trim().slice(0, 80);
  }
  if (typeof properties.invite_present === 'boolean') {
    sanitized.invite_present = properties.invite_present;
  }
  if (typeof properties.is_demo === 'boolean') {
    sanitized.is_demo = properties.is_demo;
  }
  if (typeof properties.is_share === 'boolean') {
    sanitized.is_share = properties.is_share;
  }
  if (typeof properties.song_key === 'string') {
    const songKey = properties.song_key.trim();
    if (SONG_KEY_PATTERN.test(songKey)) {
      sanitized.song_key = songKey;
    }
  }
  if (
    typeof properties.landing_path === 'string'
    && properties.landing_path.startsWith('/')
    && isSupportedLanguage(properties.landing_path.slice(1))
  ) {
    sanitized.landing_path = properties.landing_path;
  }
  if (
    typeof properties.blog_path === 'string'
    && properties.blog_path.length <= 240
    && isSupportedLanguage(BLOG_PATH_PATTERN.exec(properties.blog_path)?.[1] ?? '')
  ) {
    sanitized.blog_path = properties.blog_path;
  }
  if (
    properties.placement === 'hero'
    || properties.placement === 'header'
    || properties.placement === 'final'
  ) {
    sanitized.placement = properties.placement;
  }
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

function inferRouteKey(pathname: string): AnalyticsRouteKey | undefined {
  const normalizedPath = stripLanguageFromPathname(pathname);
  if (normalizedPath === '/') return 'landing';
  if (
    normalizedPath === '/use-cases'
    || normalizedPath.startsWith('/use-cases/')
    || normalizedPath.startsWith('/guides/')
    || normalizedPath.startsWith('/compare/')
  ) {
    return 'public_knowledge';
  }
  if (normalizedPath === '/blog') return 'blog_index';
  if (normalizedPath.startsWith('/blog/')) return 'blog_article';
  if (normalizedPath.startsWith('/practice-songs/')) return 'practice_song';
  if (normalizedPath.startsWith('/practice/')) return 'practice_room';
  if (normalizedPath === '/demo') return 'demo';
  if (normalizedPath === '/login') return 'login';
  if (normalizedPath === '/studio') return 'studio_list';
  if (normalizedPath.startsWith('/studio/')) return 'studio_room';
  if (normalizedPath.startsWith('/share/')) return 'share';
  if (normalizedPath.startsWith('/guitar-play')) return 'guitar_play';
  return undefined;
}

function buildPayload(
  eventName: AnalyticsEventName,
  properties?: AnalyticsProperties,
  routeKey?: AnalyticsRouteKey,
): AnalyticsPayload {
  const sessionId = getOrCreateAnalyticsSessionId();
  const derivedRouteKey = routeKey
    ?? (typeof window !== 'undefined' ? inferRouteKey(window.location.pathname) : undefined);
  return {
    event_id: randomEventId(),
    session_id: sessionId,
    event_name: eventName,
    source: 'frontend',
    route_key: derivedRouteKey,
    referrer_host: referrerHost(),
    utm_source: readUtmParam('utm_source'),
    utm_medium: readUtmParam('utm_medium'),
    utm_campaign: readUtmParam('utm_campaign'),
    properties: sanitizeProperties(properties),
    occurred_at: new Date().toISOString(),
  };
}

function sendPayload(payload: AnalyticsPayload): void {
  const body = JSON.stringify(payload);
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon('/api/analytics/events', blob)) {
        return;
      }
    } catch {
      /* fall through to fetch */
    }
  }

  if (typeof fetch === 'function') {
    void fetch('/api/analytics/events', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    }).catch(() => undefined);
  }
}

export function trackProductEvent(
  eventName: AnalyticsEventName,
  properties?: AnalyticsProperties,
  routeKey?: AnalyticsRouteKey,
): string {
  const payload = buildPayload(eventName, properties, routeKey);
  sendPayload(payload);
  return payload.event_id;
}

export function track(
  eventName: AnalyticsEventName,
  properties?: AnalyticsProperties,
  routeKey?: AnalyticsRouteKey,
): string {
  return trackProductEvent(eventName, properties, routeKey);
}
